import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import {
  ArrowRight, ArrowUpRight, Camera, Instagram, MapPin, Globe,
  Sparkles, Check, Star, Award, Users, TrendingUp
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

// ---------- Animations ----------
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.12, delayChildren: 0.1 } }
};

const Section = ({ children, className = '' }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  return (
    <motion.section
      ref={ref}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
};

// ---------- Inline translations (11 languages) ----------
const i18n = {
  en: {
    badge: 'Local Business Growth',
    heroTitle: 'Built for Local Businesses in New Jersey',
    heroDesc: 'We help restaurants, cafes, bakeries and retail stores grow with real on-site content, social media and Google visibility — not generic marketing.',
    servicesLabel: 'Core Services',
    customPackages: 'Custom Packages Available',
    learnMore: 'Learn More',
    s1Title: 'Professional Content Creation',
    s1Tagline: 'On-Site Photo & Video',
    s1Desc: 'We come to your business and capture real, authentic photos and videos that actually represent your brand — not stock imagery.',
    s1F1: 'On-site photo & video production',
    s1F2: 'Reels & short-form content',
    s1F3: 'Product showcases',
    s1F4: 'Restaurant & retail content',
    s2Title: 'Social Media Management',
    s2Tagline: 'Instagram Growth',
    s2Desc: 'A consistent, on-brand Instagram presence that keeps your business top-of-mind and builds a real local community around it.',
    s2F1: 'Instagram management',
    s2F2: 'Content planning',
    s2F3: 'Daily stories',
    s2F4: 'Community engagement',
    s3Title: 'Google Business Growth',
    s3Tagline: 'Local Search Visibility',
    s3Desc: 'Get found by customers searching nearby. We optimize your Google Business Profile so your business is the obvious choice in local search.',
    s3F1: 'Google Business Profile optimization',
    s3F2: 'Local search visibility',
    s3F3: 'Review strategy',
    s3F4: 'Customer discovery',
    s4Title: 'Business Websites',
    s4Tagline: 'Fast, Modern, Mobile-First',
    s4Desc: 'Clean, fast websites built for local businesses — designed to convert visitors into walk-ins, calls and orders.',
    s4F1: 'Fast modern websites',
    s4F2: 'Mobile optimization',
    s4F3: 'Contact forms',
    s4F4: 'SEO fundamentals',
    trustedLabel: 'Proof',
    trustedTitle: 'Trusted by Local Businesses',
    trustedDesc: 'Restaurants, cafes, bakeries and retail stores across New Jersey trust PIRONE Media to grow their local presence — with real content and real results.',
    trustedStat1Value: '50+',
    trustedStat1Label: 'Local Businesses',
    trustedStat2Value: '11',
    trustedStat2Label: 'Cities in NJ',
    trustedStat3Value: '4.9★',
    trustedStat3Label: 'Avg. Client Rating',
    trustedStat4Value: '24h',
    trustedStat4Label: 'Response Time',
    whyLabel: 'Why PIRONE',
    whyTitle: 'A Real Growth Partner, Not an Agency',
    why1Title: 'We Show Up',
    why1Desc: 'We visit your business in person to capture real content — no stock photos, no generic templates.',
    why2Title: 'Local Focus',
    why2Desc: 'Built specifically for New Jersey local businesses, not a generic agency playbook.',
    why3Title: 'Real Results',
    why3Desc: 'Calls, walk-ins and orders — not vanity metrics or impressions.',
    why4Title: 'Premium Quality',
    why4Desc: 'Content and strategy that makes your business feel like the best on the block.',
    ctaTitle: 'Ready to Grow Your Local Business?',
    ctaDesc: "Let's talk about a custom package built around your business, your customers and your goals.",
    ctaButton: 'Apply Now',
    ctaSecondary: 'See Local Visibility System'
  },
  tr: {
    badge: 'Yerel İşletme Büyümesi',
    heroTitle: 'New Jersey’deki Yerel İşletmeler İçin',
    heroDesc: 'Restoran, kafe, fırın ve perakende işletmelerin gerçek yerinde içerik, sosyal medya ve Google görünürlüğü ile büyümesine yardımcı oluyoruz — jenerik pazarlama değil.',
    servicesLabel: 'Temel Hizmetler',
    customPackages: 'Özel Paketler Mevcut',
    learnMore: 'Detaylar',
    s1Title: 'Profesyonel İçerik Üretimi',
    s1Tagline: 'Yerinde Fotoğraf & Video',
    s1Desc: 'İşletmenize geliyoruz ve markanızı gerçekten temsil eden özgün fotoğraf & videolar çekiyoruz — stok görsel değil.',
    s1F1: 'Yerinde fotoğraf & video çekimi',
    s1F2: 'Reels & kısa format içerik',
    s1F3: 'Ürün tanıtımları',
    s1F4: 'Restoran & perakende içeriği',
    s2Title: 'Sosyal Medya Yönetimi',
    s2Tagline: 'Instagram Büyümesi',
    s2Desc: 'İşletmenizi akılda tutan ve etrafında gerçek bir yerel topluluk oluşturan tutarlı, markaya uygun bir Instagram varlığı.',
    s2F1: 'Instagram yönetimi',
    s2F2: 'İçerik planlama',
    s2F3: 'Günlük story’ler',
    s2F4: 'Topluluk etkileşimi',
    s3Title: 'Google Business Büyümesi',
    s3Tagline: 'Yerel Arama Görünürlüğü',
    s3Desc: 'Yakındaki müşteriler tarafından bulunun. Google Business profilinizi optimize ediyoruz, böylece yerel aramada açık ara tercih oluyorsunuz.',
    s3F1: 'Google Business profil optimizasyonu',
    s3F2: 'Yerel arama görünürlüğü',
    s3F3: 'Yorum stratejisi',
    s3F4: 'Müşteri keşfi',
    s4Title: 'İşletme Web Siteleri',
    s4Tagline: 'Hızlı, Modern, Mobil Öncelikli',
    s4Desc: 'Yerel işletmeler için tasarlanmış sade, hızlı web siteleri — ziyaretçileri müşteriye, çağrıya ve siparişe dönüştürmek için.',
    s4F1: 'Hızlı, modern web siteleri',
    s4F2: 'Mobil optimizasyon',
    s4F3: 'İletişim formları',
    s4F4: 'SEO temelleri',
    trustedLabel: 'Kanıt',
    trustedTitle: 'Yerel İşletmelerin Güvendiği Marka',
    trustedDesc: 'New Jersey’deki restoranlar, kafeler, fırınlar ve perakende işletmeler yerel varlıklarını büyütmek için PIRONE Media’ya güveniyor — gerçek içerik, gerçek sonuçlar.',
    trustedStat1Value: '50+',
    trustedStat1Label: 'Yerel İşletme',
    trustedStat2Value: '11',
    trustedStat2Label: 'NJ Şehir',
    trustedStat3Value: '4.9★',
    trustedStat3Label: 'Ortalama Puan',
    trustedStat4Value: '24s',
    trustedStat4Label: 'Yanıt Süresi',
    whyLabel: 'Neden PIRONE',
    whyTitle: 'Bir Ajans Değil, Gerçek Büyüme Ortağı',
    why1Title: 'Yerine Geliyoruz',
    why1Desc: 'Gerçek içerik çekmek için işletmenize bizzat geliyoruz — stok fotoğraf, jenerik şablon yok.',
    why2Title: 'Yerel Odak',
    why2Desc: 'New Jersey’deki yerel işletmeler için özel olarak tasarlandı, jenerik bir ajans yaklaşımı değil.',
    why3Title: 'Gerçek Sonuçlar',
    why3Desc: 'Aramalar, ziyaretler, siparişler — gösterim sayıları değil.',
    why4Title: 'Premium Kalite',
    why4Desc: 'İşletmenizi mahallenin en iyisi gibi gösteren içerik ve strateji.',
    ctaTitle: 'Yerel İşletmenizi Büyütmeye Hazır mısınız?',
    ctaDesc: 'İşletmenize, müşterilerinize ve hedeflerinize özel bir paket için konuşalım.',
    ctaButton: 'Hemen Başvur',
    ctaSecondary: 'Yerel Görünürlük Sistemini Gör'
  },
  es: {
    badge: 'Crecimiento de Negocios Locales',
    heroTitle: 'Hecho para Negocios Locales en New Jersey',
    heroDesc: 'Ayudamos a restaurantes, cafeterías, panaderías y tiendas a crecer con contenido real en sitio, redes sociales y visibilidad en Google — no marketing genérico.',
    servicesLabel: 'Servicios Principales',
    customPackages: 'Paquetes Personalizados Disponibles',
    learnMore: 'Más Información',
    s1Title: 'Creación de Contenido Profesional',
    s1Tagline: 'Foto y Video en Sitio',
    s1Desc: 'Vamos a tu negocio y capturamos fotos y videos reales que representan tu marca — sin imágenes de stock.',
    s1F1: 'Producción de foto y video en sitio',
    s1F2: 'Reels y contenido corto',
    s1F3: 'Muestras de productos',
    s1F4: 'Contenido para restaurantes y tiendas',
    s2Title: 'Gestión de Redes Sociales',
    s2Tagline: 'Crecimiento en Instagram',
    s2Desc: 'Una presencia constante y coherente en Instagram que mantiene tu negocio presente y construye una comunidad local real.',
    s2F1: 'Gestión de Instagram',
    s2F2: 'Planificación de contenido',
    s2F3: 'Historias diarias',
    s2F4: 'Interacción con la comunidad',
    s3Title: 'Crecimiento en Google Business',
    s3Tagline: 'Visibilidad en Búsqueda Local',
    s3Desc: 'Que te encuentren los clientes cercanos. Optimizamos tu perfil de Google Business para que seas la opción evidente.',
    s3F1: 'Optimización del Perfil de Google Business',
    s3F2: 'Visibilidad en búsqueda local',
    s3F3: 'Estrategia de reseñas',
    s3F4: 'Descubrimiento de clientes',
    s4Title: 'Sitios Web para Negocios',
    s4Tagline: 'Rápidos, Modernos, Mobile-First',
    s4Desc: 'Sitios web limpios y rápidos hechos para negocios locales — diseñados para convertir visitas en clientes.',
    s4F1: 'Sitios web rápidos y modernos',
    s4F2: 'Optimización móvil',
    s4F3: 'Formularios de contacto',
    s4F4: 'Fundamentos de SEO',
    trustedLabel: 'Prueba',
    trustedTitle: 'Confiado por Negocios Locales',
    trustedDesc: 'Restaurantes, cafeterías, panaderías y tiendas en New Jersey confían en PIRONE Media — contenido real y resultados reales.',
    trustedStat1Value: '50+', trustedStat1Label: 'Negocios Locales',
    trustedStat2Value: '11', trustedStat2Label: 'Ciudades en NJ',
    trustedStat3Value: '4.9★', trustedStat3Label: 'Calificación Promedio',
    trustedStat4Value: '24h', trustedStat4Label: 'Tiempo de Respuesta',
    whyLabel: 'Por Qué PIRONE',
    whyTitle: 'Un Socio Real de Crecimiento, No una Agencia',
    why1Title: 'Vamos a Tu Lugar',
    why1Desc: 'Visitamos tu negocio en persona para capturar contenido real.',
    why2Title: 'Enfoque Local',
    why2Desc: 'Diseñado específicamente para negocios locales de New Jersey.',
    why3Title: 'Resultados Reales',
    why3Desc: 'Llamadas, visitas y pedidos — no métricas de vanidad.',
    why4Title: 'Calidad Premium',
    why4Desc: 'Contenido y estrategia que hacen que tu negocio destaque.',
    ctaTitle: '¿Listo para Crecer tu Negocio Local?',
    ctaDesc: 'Hablemos de un paquete personalizado para tu negocio.',
    ctaButton: 'Aplicar Ahora',
    ctaSecondary: 'Ver Sistema de Visibilidad Local'
  },
  pt: {
    badge: 'Crescimento de Negócios Locais',
    heroTitle: 'Feito para Negócios Locais em New Jersey',
    heroDesc: 'Ajudamos restaurantes, cafés, padarias e lojas a crescer com conteúdo real no local, redes sociais e visibilidade no Google — não marketing genérico.',
    servicesLabel: 'Serviços Principais',
    customPackages: 'Pacotes Personalizados Disponíveis',
    learnMore: 'Saber Mais',
    s1Title: 'Criação de Conteúdo Profissional',
    s1Tagline: 'Foto e Vídeo no Local',
    s1Desc: 'Vamos ao seu negócio e capturamos fotos e vídeos reais que representam sua marca.',
    s1F1: 'Produção de foto e vídeo no local', s1F2: 'Reels e conteúdo curto', s1F3: 'Demonstração de produtos', s1F4: 'Conteúdo para restaurantes e lojas',
    s2Title: 'Gestão de Redes Sociais', s2Tagline: 'Crescimento no Instagram',
    s2Desc: 'Uma presença consistente no Instagram que mantém seu negócio em destaque.',
    s2F1: 'Gestão do Instagram', s2F2: 'Planejamento de conteúdo', s2F3: 'Stories diários', s2F4: 'Engajamento com a comunidade',
    s3Title: 'Crescimento no Google Business', s3Tagline: 'Visibilidade em Busca Local',
    s3Desc: 'Seja encontrado pelos clientes próximos. Otimizamos seu perfil do Google Business.',
    s3F1: 'Otimização do Perfil Google Business', s3F2: 'Visibilidade em busca local', s3F3: 'Estratégia de avaliações', s3F4: 'Descoberta de clientes',
    s4Title: 'Sites para Negócios', s4Tagline: 'Rápidos, Modernos, Mobile-First',
    s4Desc: 'Sites limpos e rápidos para negócios locais — projetados para converter visitantes em clientes.',
    s4F1: 'Sites rápidos e modernos', s4F2: 'Otimização mobile', s4F3: 'Formulários de contato', s4F4: 'Fundamentos de SEO',
    trustedLabel: 'Prova', trustedTitle: 'Confiado por Negócios Locais',
    trustedDesc: 'Restaurantes, cafés, padarias e lojas em New Jersey confiam na PIRONE Media.',
    trustedStat1Value: '50+', trustedStat1Label: 'Negócios Locais',
    trustedStat2Value: '11', trustedStat2Label: 'Cidades em NJ',
    trustedStat3Value: '4.9★', trustedStat3Label: 'Avaliação Média',
    trustedStat4Value: '24h', trustedStat4Label: 'Tempo de Resposta',
    whyLabel: 'Por Que PIRONE', whyTitle: 'Um Parceiro Real, Não uma Agência',
    why1Title: 'Vamos ao Local', why1Desc: 'Visitamos seu negócio pessoalmente para criar conteúdo real.',
    why2Title: 'Foco Local', why2Desc: 'Feito para negócios locais de New Jersey.',
    why3Title: 'Resultados Reais', why3Desc: 'Ligações, visitas e pedidos — não métricas de vaidade.',
    why4Title: 'Qualidade Premium', why4Desc: 'Conteúdo que faz seu negócio se destacar.',
    ctaTitle: 'Pronto para Crescer seu Negócio Local?',
    ctaDesc: 'Vamos falar sobre um pacote personalizado.',
    ctaButton: 'Candidatar-se Agora', ctaSecondary: 'Ver Sistema de Visibilidade Local'
  },
  it: {
    badge: 'Crescita Attività Locali',
    heroTitle: 'Pensato per le Attività Locali in New Jersey',
    heroDesc: 'Aiutiamo ristoranti, caffè, panetterie e negozi a crescere con contenuti reali sul posto, social e visibilità su Google — non marketing generico.',
    servicesLabel: 'Servizi Principali', customPackages: 'Pacchetti Personalizzati Disponibili', learnMore: 'Scopri di Più',
    s1Title: 'Creazione di Contenuti Professionali', s1Tagline: 'Foto e Video sul Posto',
    s1Desc: 'Veniamo nella tua attività e creiamo foto e video reali che rappresentano davvero il tuo brand.',
    s1F1: 'Produzione foto e video sul posto', s1F2: 'Reels e contenuti brevi', s1F3: 'Vetrine prodotti', s1F4: 'Contenuti per ristoranti e negozi',
    s2Title: 'Gestione Social Media', s2Tagline: 'Crescita Instagram',
    s2Desc: 'Una presenza Instagram coerente che mantiene la tua attività al centro dell\'attenzione.',
    s2F1: 'Gestione Instagram', s2F2: 'Pianificazione contenuti', s2F3: 'Storie quotidiane', s2F4: 'Coinvolgimento community',
    s3Title: 'Crescita Google Business', s3Tagline: 'Visibilità Ricerca Locale',
    s3Desc: 'Fatti trovare dai clienti vicini. Ottimizziamo il tuo profilo Google Business.',
    s3F1: 'Ottimizzazione Profilo Google Business', s3F2: 'Visibilità ricerca locale', s3F3: 'Strategia recensioni', s3F4: 'Scoperta clienti',
    s4Title: 'Siti Web Aziendali', s4Tagline: 'Veloci, Moderni, Mobile-First',
    s4Desc: 'Siti web puliti e veloci per le attività locali — progettati per convertire i visitatori in clienti.',
    s4F1: 'Siti web veloci e moderni', s4F2: 'Ottimizzazione mobile', s4F3: 'Moduli di contatto', s4F4: 'Fondamenti SEO',
    trustedLabel: 'Prova', trustedTitle: 'Scelti dalle Attività Locali',
    trustedDesc: 'Ristoranti, caffè, panetterie e negozi in New Jersey si fidano di PIRONE Media.',
    trustedStat1Value: '50+', trustedStat1Label: 'Attività Locali',
    trustedStat2Value: '11', trustedStat2Label: 'Città in NJ',
    trustedStat3Value: '4.9★', trustedStat3Label: 'Valutazione Media',
    trustedStat4Value: '24h', trustedStat4Label: 'Tempo di Risposta',
    whyLabel: 'Perché PIRONE', whyTitle: 'Un Vero Partner di Crescita, Non un\'Agenzia',
    why1Title: 'Veniamo da Te', why1Desc: 'Visitiamo la tua attività di persona per creare contenuti reali.',
    why2Title: 'Focus Locale', why2Desc: 'Costruito specificamente per attività locali del New Jersey.',
    why3Title: 'Risultati Reali', why3Desc: 'Chiamate, visite e ordini — non metriche di vanità.',
    why4Title: 'Qualità Premium', why4Desc: 'Contenuti che fanno spiccare la tua attività.',
    ctaTitle: 'Pronto a Far Crescere la Tua Attività Locale?',
    ctaDesc: 'Parliamo di un pacchetto personalizzato.',
    ctaButton: 'Candidati Ora', ctaSecondary: 'Vedi Sistema di Visibilità Locale'
  },
  ar: {
    badge: 'نمو الأعمال المحلية',
    heroTitle: 'مصمم للأعمال المحلية في نيو جيرسي',
    heroDesc: 'نساعد المطاعم والمقاهي والمخابز والمتاجر على النمو بمحتوى حقيقي في الموقع ووسائل التواصل وظهور Google.',
    servicesLabel: 'الخدمات الأساسية', customPackages: 'باقات مخصصة متوفرة', learnMore: 'اعرف المزيد',
    s1Title: 'إنشاء محتوى احترافي', s1Tagline: 'تصوير في الموقع',
    s1Desc: 'نأتي إلى عملك ونلتقط صورًا ومقاطع فيديو حقيقية تمثل علامتك التجارية.',
    s1F1: 'إنتاج صور وفيديو في الموقع', s1F2: 'Reels ومحتوى قصير', s1F3: 'عرض المنتجات', s1F4: 'محتوى للمطاعم والمتاجر',
    s2Title: 'إدارة وسائل التواصل', s2Tagline: 'نمو Instagram',
    s2Desc: 'حضور Instagram متسق يجعل عملك في مقدمة اهتمام العملاء.',
    s2F1: 'إدارة Instagram', s2F2: 'تخطيط المحتوى', s2F3: 'قصص يومية', s2F4: 'تفاعل مع المجتمع',
    s3Title: 'نمو Google Business', s3Tagline: 'ظهور البحث المحلي',
    s3Desc: 'دع العملاء القريبين يجدونك. نحسن ملفك على Google Business.',
    s3F1: 'تحسين ملف Google Business', s3F2: 'ظهور في البحث المحلي', s3F3: 'استراتيجية المراجعات', s3F4: 'اكتشاف العملاء',
    s4Title: 'مواقع للأعمال', s4Tagline: 'سريعة، حديثة، تركز على الجوال',
    s4Desc: 'مواقع نظيفة وسريعة للأعمال المحلية — مصممة لتحويل الزوار إلى عملاء.',
    s4F1: 'مواقع سريعة وحديثة', s4F2: 'تحسين للجوال', s4F3: 'نماذج اتصال', s4F4: 'أساسيات SEO',
    trustedLabel: 'إثبات', trustedTitle: 'موثوق من الأعمال المحلية',
    trustedDesc: 'المطاعم والمقاهي والمخابز والمتاجر في نيو جيرسي تثق في PIRONE Media.',
    trustedStat1Value: '+50', trustedStat1Label: 'عمل محلي',
    trustedStat2Value: '11', trustedStat2Label: 'مدينة في NJ',
    trustedStat3Value: '4.9★', trustedStat3Label: 'متوسط التقييم',
    trustedStat4Value: '24س', trustedStat4Label: 'وقت الاستجابة',
    whyLabel: 'لماذا PIRONE', whyTitle: 'شريك نمو حقيقي، وليس مجرد وكالة',
    why1Title: 'نأتي إليك', why1Desc: 'نزور عملك شخصيًا لإنشاء محتوى حقيقي.',
    why2Title: 'تركيز محلي', why2Desc: 'مصمم خصيصًا لأعمال نيو جيرسي المحلية.',
    why3Title: 'نتائج حقيقية', why3Desc: 'مكالمات وزيارات وطلبات — وليس مقاييس وهمية.',
    why4Title: 'جودة متميزة', why4Desc: 'محتوى يجعل عملك يتميز.',
    ctaTitle: 'مستعد لتنمية عملك المحلي؟', ctaDesc: 'لنتحدث عن باقة مخصصة لك.',
    ctaButton: 'قدم الآن', ctaSecondary: 'شاهد نظام الظهور المحلي'
  },
  hi: {
    badge: 'स्थानीय व्यवसाय विकास',
    heroTitle: 'न्यू जर्सी के स्थानीय व्यवसायों के लिए बनाया गया',
    heroDesc: 'हम रेस्तरां, कैफ़े, बेकरी और स्टोर को साइट पर वास्तविक सामग्री, सोशल मीडिया और Google दृश्यता के साथ बढ़ने में मदद करते हैं।',
    servicesLabel: 'मुख्य सेवाएं', customPackages: 'कस्टम पैकेज उपलब्ध', learnMore: 'अधिक जानें',
    s1Title: 'पेशेवर सामग्री निर्माण', s1Tagline: 'साइट पर फोटो और वीडियो',
    s1Desc: 'हम आपके व्यवसाय में आते हैं और वास्तविक फोटो और वीडियो लेते हैं जो आपके ब्रांड का प्रतिनिधित्व करते हैं।',
    s1F1: 'साइट पर फोटो और वीडियो उत्पादन', s1F2: 'Reels और छोटी सामग्री', s1F3: 'उत्पाद प्रदर्शन', s1F4: 'रेस्तरां और स्टोर सामग्री',
    s2Title: 'सोशल मीडिया प्रबंधन', s2Tagline: 'Instagram विकास',
    s2Desc: 'सुसंगत Instagram उपस्थिति जो आपके व्यवसाय को ध्यान में रखती है।',
    s2F1: 'Instagram प्रबंधन', s2F2: 'सामग्री योजना', s2F3: 'दैनिक स्टोरीज़', s2F4: 'समुदाय सहभागिता',
    s3Title: 'Google Business विकास', s3Tagline: 'स्थानीय खोज दृश्यता',
    s3Desc: 'पास के ग्राहकों द्वारा खोजे जाएं। हम आपकी Google Business प्रोफ़ाइल को अनुकूलित करते हैं।',
    s3F1: 'Google Business प्रोफ़ाइल अनुकूलन', s3F2: 'स्थानीय खोज दृश्यता', s3F3: 'समीक्षा रणनीति', s3F4: 'ग्राहक खोज',
    s4Title: 'व्यवसाय वेबसाइट', s4Tagline: 'तेज़, आधुनिक, मोबाइल-फर्स्ट',
    s4Desc: 'स्थानीय व्यवसायों के लिए साफ, तेज़ वेबसाइटें — विज़िटर्स को ग्राहकों में बदलने के लिए डिज़ाइन की गई।',
    s4F1: 'तेज़, आधुनिक वेबसाइटें', s4F2: 'मोबाइल अनुकूलन', s4F3: 'संपर्क फ़ॉर्म', s4F4: 'SEO मूल बातें',
    trustedLabel: 'प्रमाण', trustedTitle: 'स्थानीय व्यवसायों द्वारा भरोसा',
    trustedDesc: 'न्यू जर्सी के रेस्तरां, कैफ़े, बेकरी और स्टोर PIRONE Media पर भरोसा करते हैं।',
    trustedStat1Value: '50+', trustedStat1Label: 'स्थानीय व्यवसाय',
    trustedStat2Value: '11', trustedStat2Label: 'NJ में शहर',
    trustedStat3Value: '4.9★', trustedStat3Label: 'औसत रेटिंग',
    trustedStat4Value: '24घं', trustedStat4Label: 'प्रतिक्रिया समय',
    whyLabel: 'क्यों PIRONE', whyTitle: 'एक वास्तविक विकास साझेदार, एजेंसी नहीं',
    why1Title: 'हम आते हैं', why1Desc: 'वास्तविक सामग्री बनाने के लिए हम व्यक्तिगत रूप से आते हैं।',
    why2Title: 'स्थानीय फोकस', why2Desc: 'न्यू जर्सी के स्थानीय व्यवसायों के लिए विशेष रूप से बनाया गया।',
    why3Title: 'वास्तविक परिणाम', why3Desc: 'कॉल, विज़िट और ऑर्डर — झूठे मेट्रिक्स नहीं।',
    why4Title: 'प्रीमियम गुणवत्ता', why4Desc: 'सामग्री जो आपके व्यवसाय को अलग बनाती है।',
    ctaTitle: 'अपना स्थानीय व्यवसाय बढ़ाने के लिए तैयार हैं?', ctaDesc: 'एक कस्टम पैकेज के बारे में बात करते हैं।',
    ctaButton: 'अभी आवेदन करें', ctaSecondary: 'स्थानीय दृश्यता प्रणाली देखें'
  },
  ko: {
    badge: '로컬 비즈니스 성장',
    heroTitle: '뉴저지 로컬 비즈니스를 위해 만들어졌습니다',
    heroDesc: '레스토랑, 카페, 베이커리 및 매장이 현장의 실제 콘텐츠, 소셜 미디어 및 Google 가시성으로 성장하도록 돕습니다.',
    servicesLabel: '핵심 서비스', customPackages: '맞춤 패키지 이용 가능', learnMore: '자세히 보기',
    s1Title: '전문 콘텐츠 제작', s1Tagline: '현장 사진 및 동영상',
    s1Desc: '비즈니스를 방문하여 브랜드를 실제로 대표하는 진짜 사진과 동영상을 촬영합니다.',
    s1F1: '현장 사진 및 동영상 제작', s1F2: 'Reels 및 숏폼 콘텐츠', s1F3: '제품 쇼케이스', s1F4: '레스토랑 및 매장 콘텐츠',
    s2Title: '소셜 미디어 관리', s2Tagline: 'Instagram 성장',
    s2Desc: '비즈니스를 부각시키는 일관된 Instagram 존재감.',
    s2F1: 'Instagram 관리', s2F2: '콘텐츠 기획', s2F3: '일일 스토리', s2F4: '커뮤니티 참여',
    s3Title: 'Google Business 성장', s3Tagline: '로컬 검색 가시성',
    s3Desc: '근처 고객에게 발견되도록 Google Business 프로필을 최적화합니다.',
    s3F1: 'Google Business 프로필 최적화', s3F2: '로컬 검색 가시성', s3F3: '리뷰 전략', s3F4: '고객 발견',
    s4Title: '비즈니스 웹사이트', s4Tagline: '빠르고 현대적인 모바일 우선',
    s4Desc: '로컬 비즈니스를 위한 깔끔하고 빠른 웹사이트 — 방문자를 고객으로 전환하도록 설계되었습니다.',
    s4F1: '빠르고 현대적인 웹사이트', s4F2: '모바일 최적화', s4F3: '연락 양식', s4F4: 'SEO 기본',
    trustedLabel: '증명', trustedTitle: '로컬 비즈니스가 신뢰합니다',
    trustedDesc: '뉴저지의 레스토랑, 카페, 베이커리 및 매장이 PIRONE Media를 신뢰합니다.',
    trustedStat1Value: '50+', trustedStat1Label: '로컬 비즈니스',
    trustedStat2Value: '11', trustedStat2Label: 'NJ 도시',
    trustedStat3Value: '4.9★', trustedStat3Label: '평균 평점',
    trustedStat4Value: '24시간', trustedStat4Label: '응답 시간',
    whyLabel: '왜 PIRONE인가', whyTitle: '대행사가 아닌 진정한 성장 파트너',
    why1Title: '직접 방문합니다', why1Desc: '실제 콘텐츠를 캡처하기 위해 직접 방문합니다.',
    why2Title: '로컬 포커스', why2Desc: '뉴저지 로컬 비즈니스를 위해 특별히 제작되었습니다.',
    why3Title: '실제 결과', why3Desc: '통화, 방문, 주문 — 허영 메트릭이 아닙니다.',
    why4Title: '프리미엄 품질', why4Desc: '비즈니스를 돋보이게 만드는 콘텐츠.',
    ctaTitle: '로컬 비즈니스를 성장시킬 준비가 되셨나요?', ctaDesc: '맞춤 패키지에 대해 이야기해 봅시다.',
    ctaButton: '지금 신청', ctaSecondary: '로컬 가시성 시스템 보기'
  },
  zh: {
    badge: '本地业务增长',
    heroTitle: '为新泽西本地企业打造',
    heroDesc: '我们帮助餐厅、咖啡馆、面包店和零售店通过真实的现场内容、社交媒体和Google可见性实现增长。',
    servicesLabel: '核心服务', customPackages: '提供定制套餐', learnMore: '了解更多',
    s1Title: '专业内容创作', s1Tagline: '现场摄影和视频',
    s1Desc: '我们前往您的企业，拍摄真正代表您品牌的真实照片和视频。',
    s1F1: '现场照片和视频制作', s1F2: 'Reels和短内容', s1F3: '产品展示', s1F4: '餐厅和零售内容',
    s2Title: '社交媒体管理', s2Tagline: 'Instagram增长',
    s2Desc: '一致的Instagram存在感，让您的企业保持在最前沿。',
    s2F1: 'Instagram管理', s2F2: '内容规划', s2F3: '每日故事', s2F4: '社区参与',
    s3Title: 'Google Business增长', s3Tagline: '本地搜索可见性',
    s3Desc: '让附近的客户找到您。我们优化您的Google Business个人资料。',
    s3F1: 'Google Business个人资料优化', s3F2: '本地搜索可见性', s3F3: '评论策略', s3F4: '客户发现',
    s4Title: '商业网站', s4Tagline: '快速、现代、移动优先',
    s4Desc: '为本地企业打造的简洁、快速的网站 — 旨在将访客转化为客户。',
    s4F1: '快速、现代的网站', s4F2: '移动优化', s4F3: '联系表单', s4F4: 'SEO基础',
    trustedLabel: '证明', trustedTitle: '本地企业的信任之选',
    trustedDesc: '新泽西的餐厅、咖啡馆、面包店和零售店信赖PIRONE Media。',
    trustedStat1Value: '50+', trustedStat1Label: '本地企业',
    trustedStat2Value: '11', trustedStat2Label: 'NJ城市',
    trustedStat3Value: '4.9★', trustedStat3Label: '平均评分',
    trustedStat4Value: '24小时', trustedStat4Label: '响应时间',
    whyLabel: '为什么选择PIRONE', whyTitle: '真正的增长伙伴，而不是代理机构',
    why1Title: '我们亲自到场', why1Desc: '我们亲自访问您的企业以捕捉真实内容。',
    why2Title: '本地专注', why2Desc: '专为新泽西本地企业打造。',
    why3Title: '真实结果', why3Desc: '电话、到店和订单 — 而不是虚荣指标。',
    why4Title: '高级品质', why4Desc: '让您的企业脱颖而出的内容。',
    ctaTitle: '准备好发展您的本地业务了吗？', ctaDesc: '让我们谈谈定制套餐。',
    ctaButton: '立即申请', ctaSecondary: '查看本地可见性系统'
  },
  el: {
    badge: 'Ανάπτυξη Τοπικών Επιχειρήσεων',
    heroTitle: 'Σχεδιασμένο για Τοπικές Επιχειρήσεις στο Νιου Τζέρσεϊ',
    heroDesc: 'Βοηθάμε εστιατόρια, καφέ, αρτοποιεία και καταστήματα να αναπτυχθούν με πραγματικό περιεχόμενο επιτόπου.',
    servicesLabel: 'Βασικές Υπηρεσίες', customPackages: 'Διαθέσιμα Προσαρμοσμένα Πακέτα', learnMore: 'Μάθετε Περισσότερα',
    s1Title: 'Επαγγελματική Δημιουργία Περιεχομένου', s1Tagline: 'Φωτογραφία & Βίντεο Επιτόπου',
    s1Desc: 'Ερχόμαστε στην επιχείρησή σας και τραβάμε πραγματικές φωτογραφίες και βίντεο που εκπροσωπούν το brand σας.',
    s1F1: 'Παραγωγή φωτογραφιών & βίντεο επιτόπου', s1F2: 'Reels και σύντομο περιεχόμενο', s1F3: 'Προβολές προϊόντων', s1F4: 'Περιεχόμενο για εστιατόρια και καταστήματα',
    s2Title: 'Διαχείριση Μέσων Κοινωνικής Δικτύωσης', s2Tagline: 'Ανάπτυξη Instagram',
    s2Desc: 'Συνεπής παρουσία Instagram που κρατά την επιχείρησή σας στο επίκεντρο.',
    s2F1: 'Διαχείριση Instagram', s2F2: 'Σχεδιασμός περιεχομένου', s2F3: 'Καθημερινά stories', s2F4: 'Αλληλεπίδραση με την κοινότητα',
    s3Title: 'Ανάπτυξη Google Business', s3Tagline: 'Τοπική Ορατότητα Αναζήτησης',
    s3Desc: 'Βρεθείτε από κοντινούς πελάτες. Βελτιστοποιούμε το προφίλ σας Google Business.',
    s3F1: 'Βελτιστοποίηση Προφίλ Google Business', s3F2: 'Τοπική ορατότητα αναζήτησης', s3F3: 'Στρατηγική αξιολογήσεων', s3F4: 'Ανακάλυψη πελατών',
    s4Title: 'Επαγγελματικοί Ιστότοποι', s4Tagline: 'Γρήγοροι, Μοντέρνοι, Mobile-First',
    s4Desc: 'Καθαροί, γρήγοροι ιστότοποι για τοπικές επιχειρήσεις.',
    s4F1: 'Γρήγοροι, μοντέρνοι ιστότοποι', s4F2: 'Βελτιστοποίηση mobile', s4F3: 'Φόρμες επικοινωνίας', s4F4: 'Βασικές αρχές SEO',
    trustedLabel: 'Απόδειξη', trustedTitle: 'Με την Εμπιστοσύνη Τοπικών Επιχειρήσεων',
    trustedDesc: 'Εστιατόρια, καφέ, αρτοποιεία και καταστήματα στο Νιου Τζέρσεϊ εμπιστεύονται την PIRONE Media.',
    trustedStat1Value: '50+', trustedStat1Label: 'Τοπικές Επιχειρήσεις',
    trustedStat2Value: '11', trustedStat2Label: 'Πόλεις στο NJ',
    trustedStat3Value: '4.9★', trustedStat3Label: 'Μέση Αξιολόγηση',
    trustedStat4Value: '24ώ', trustedStat4Label: 'Χρόνος Απάντησης',
    whyLabel: 'Γιατί PIRONE', whyTitle: 'Ένας Πραγματικός Συνεργάτης Ανάπτυξης',
    why1Title: 'Ερχόμαστε σε Εσάς', why1Desc: 'Επισκεπτόμαστε την επιχείρησή σας προσωπικά.',
    why2Title: 'Τοπική Εστίαση', why2Desc: 'Σχεδιασμένο ειδικά για τοπικές επιχειρήσεις του New Jersey.',
    why3Title: 'Πραγματικά Αποτελέσματα', why3Desc: 'Κλήσεις, επισκέψεις και παραγγελίες.',
    why4Title: 'Premium Ποιότητα', why4Desc: 'Περιεχόμενο που κάνει την επιχείρησή σας να ξεχωρίζει.',
    ctaTitle: 'Έτοιμοι να Αναπτύξετε την Τοπική σας Επιχείρηση;', ctaDesc: 'Ας μιλήσουμε για ένα προσαρμοσμένο πακέτο.',
    ctaButton: 'Υποβάλετε Αίτηση Τώρα', ctaSecondary: 'Δείτε το Σύστημα Τοπικής Ορατότητας'
  },
  gu: {
    badge: 'સ્થાનિક વ્યવસાય વૃદ્ધિ',
    heroTitle: 'ન્યુ જર્સીના સ્થાનિક વ્યવસાયો માટે બનાવેલ',
    heroDesc: 'અમે રેસ્ટોરન્ટ, કેફે, બેકરી અને સ્ટોર્સને વાસ્તવિક સાઇટ સામગ્રી, સોશિયલ મીડિયા અને Google દૃશ્યતા સાથે વધવામાં મદદ કરીએ છીએ.',
    servicesLabel: 'મુખ્ય સેવાઓ', customPackages: 'કસ્ટમ પેકેજ ઉપલબ્ધ', learnMore: 'વધુ જાણો',
    s1Title: 'વ્યાવસાયિક સામગ્રી નિર્માણ', s1Tagline: 'સાઇટ પર ફોટો અને વિડિયો',
    s1Desc: 'અમે તમારા વ્યવસાય પર આવીએ છીએ અને તમારી બ્રાન્ડનું પ્રતિનિધિત્વ કરતા વાસ્તવિક ફોટા અને વિડિયો કેપ્ચર કરીએ છીએ.',
    s1F1: 'સાઇટ પર ફોટો અને વિડિયો ઉત્પાદન', s1F2: 'Reels અને ટૂંકી સામગ્રી', s1F3: 'ઉત્પાદન શોકેસ', s1F4: 'રેસ્ટોરન્ટ અને સ્ટોર સામગ્રી',
    s2Title: 'સોશિયલ મીડિયા મેનેજમેન્ટ', s2Tagline: 'Instagram વૃદ્ધિ',
    s2Desc: 'એક સુસંગત Instagram હાજરી જે તમારા વ્યવસાયને ધ્યાનમાં રાખે છે.',
    s2F1: 'Instagram મેનેજમેન્ટ', s2F2: 'સામગ્રી આયોજન', s2F3: 'દૈનિક સ્ટોરીઝ', s2F4: 'સમુદાય જોડાણ',
    s3Title: 'Google Business વૃદ્ધિ', s3Tagline: 'સ્થાનિક શોધ દૃશ્યતા',
    s3Desc: 'નજીકના ગ્રાહકો દ્વારા શોધાઓ. અમે તમારી Google Business પ્રોફાઇલને ઑપ્ટિમાઇઝ કરીએ છીએ.',
    s3F1: 'Google Business પ્રોફાઇલ ઑપ્ટિમાઇઝેશન', s3F2: 'સ્થાનિક શોધ દૃશ્યતા', s3F3: 'સમીક્ષા વ્યૂહરચના', s3F4: 'ગ્રાહક શોધ',
    s4Title: 'વ્યવસાય વેબસાઇટ્સ', s4Tagline: 'ઝડપી, આધુનિક, મોબાઇલ-ફર્સ્ટ',
    s4Desc: 'સ્થાનિક વ્યવસાયો માટે સ્વચ્છ, ઝડપી વેબસાઇટ્સ.',
    s4F1: 'ઝડપી, આધુનિક વેબસાઇટ્સ', s4F2: 'મોબાઇલ ઑપ્ટિમાઇઝેશન', s4F3: 'સંપર્ક ફોર્મ્સ', s4F4: 'SEO મૂળભૂત',
    trustedLabel: 'પુરાવો', trustedTitle: 'સ્થાનિક વ્યવસાયો દ્વારા વિશ્વસનીય',
    trustedDesc: 'ન્યુ જર્સીના રેસ્ટોરન્ટ, કેફે, બેકરી અને સ્ટોર્સ PIRONE Media પર વિશ્વાસ કરે છે.',
    trustedStat1Value: '50+', trustedStat1Label: 'સ્થાનિક વ્યવસાય',
    trustedStat2Value: '11', trustedStat2Label: 'NJ શહેરો',
    trustedStat3Value: '4.9★', trustedStat3Label: 'સરેરાશ રેટિંગ',
    trustedStat4Value: '24ક', trustedStat4Label: 'પ્રતિસાદ સમય',
    whyLabel: 'PIRONE કેમ', whyTitle: 'એક વાસ્તવિક વૃદ્ધિ ભાગીદાર, એજન્સી નહીં',
    why1Title: 'અમે આવીએ છીએ', why1Desc: 'વાસ્તવિક સામગ્રી માટે અમે વ્યક્તિગત રીતે મુલાકાત લઈએ છીએ.',
    why2Title: 'સ્થાનિક ધ્યાન', why2Desc: 'ન્યુ જર્સીના સ્થાનિક વ્યવસાયો માટે ખાસ બનાવેલ.',
    why3Title: 'વાસ્તવિક પરિણામો', why3Desc: 'કૉલ્સ, મુલાકાતો અને ઓર્ડર્સ.',
    why4Title: 'પ્રીમિયમ ગુણવત્તા', why4Desc: 'તમારા વ્યવસાયને અલગ દેખાડતી સામગ્રી.',
    ctaTitle: 'તમારા સ્થાનિક વ્યવસાયને વધારવા તૈયાર છો?', ctaDesc: 'કસ્ટમ પેકેજ વિશે વાત કરીએ.',
    ctaButton: 'હમણાં અરજી કરો', ctaSecondary: 'સ્થાનિક દૃશ્યતા સિસ્ટમ જુઓ'
  }
};

// ---------- Component ----------
const ServicesPage = () => {
  const { language } = useLanguage();
  const t = i18n[language] || i18n.en;

  const services = [
    {
      id: 'content-creation',
      icon: Camera,
      title: t.s1Title,
      tagline: t.s1Tagline,
      desc: t.s1Desc,
      features: [t.s1F1, t.s1F2, t.s1F3, t.s1F4],
      color: '#F59E0B',
      gradient: 'from-amber-500/15 to-orange-500/5',
      href: '/new-jersey'
    },
    {
      id: 'social-media',
      icon: Instagram,
      title: t.s2Title,
      tagline: t.s2Tagline,
      desc: t.s2Desc,
      features: [t.s2F1, t.s2F2, t.s2F3, t.s2F4],
      color: '#EC4899',
      gradient: 'from-pink-500/15 to-rose-500/5',
      href: '/services/social-media-management'
    },
    {
      id: 'google-business',
      icon: MapPin,
      title: t.s3Title,
      tagline: t.s3Tagline,
      desc: t.s3Desc,
      features: [t.s3F1, t.s3F2, t.s3F3, t.s3F4],
      color: '#10B981',
      gradient: 'from-emerald-500/15 to-green-500/5',
      href: '/new-jersey'
    },
    {
      id: 'business-website',
      icon: Globe,
      title: t.s4Title,
      tagline: t.s4Tagline,
      desc: t.s4Desc,
      features: [t.s4F1, t.s4F2, t.s4F3, t.s4F4],
      color: '#38BDF8',
      gradient: 'from-sky-500/15 to-cyan-500/5',
      href: '/web-design'
    }
  ];

  const trustedStats = [
    { icon: Users, value: t.trustedStat1Value, label: t.trustedStat1Label },
    { icon: MapPin, value: t.trustedStat2Value, label: t.trustedStat2Label },
    { icon: Star, value: t.trustedStat3Value, label: t.trustedStat3Label },
    { icon: TrendingUp, value: t.trustedStat4Value, label: t.trustedStat4Label }
  ];

  const whyFeatures = [
    { icon: MapPin, title: t.why1Title, desc: t.why1Desc },
    { icon: Award, title: t.why2Title, desc: t.why2Desc },
    { icon: TrendingUp, title: t.why3Title, desc: t.why3Desc },
    { icon: Sparkles, title: t.why4Title, desc: t.why4Desc }
  ];

  return (
    <div className="min-h-screen bg-transparent pt-20" data-testid="services-page">
      {/* Hero */}
      <Section className="py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-[1400px] h-[800px]"
            style={{ background: 'radial-gradient(ellipse at center top, rgba(16, 185, 129, 0.08) 0%, transparent 60%)' }}
          />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div variants={fadeInUp} className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 mb-8">
              <MapPin size={16} className="text-emerald-400" />
              <span className="text-sm font-medium text-emerald-400">
                {t.badge}
              </span>
            </div>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-semibold text-white mb-6 tracking-tight">
              {t.heroTitle}
            </h1>
            <p className="text-xl md:text-2xl text-white/50 max-w-3xl mx-auto leading-relaxed">
              {t.heroDesc}
            </p>
          </motion.div>
        </div>
      </Section>

      {/* 4 Premium Service Cards */}
      <Section className="pb-24 relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div variants={fadeInUp} className="mb-10 flex items-center justify-between flex-wrap gap-4">
            <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-white/50">
              {t.servicesLabel}
            </span>
            <span className="text-sm text-white/40 font-mono">04 / 04</span>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {services.map((service) => (
              <motion.div key={service.id} variants={fadeInUp}>
                <Link
                  to={service.href}
                  className="group block relative h-full"
                  data-testid={`service-card-${service.id}`}
                >
                  <div className={`relative h-full p-8 lg:p-10 rounded-3xl bg-gradient-to-br ${service.gradient} border border-white/[0.08] overflow-hidden transition-all duration-500 hover:border-white/[0.18] hover:shadow-2xl hover:shadow-black/30`}>
                    {/* Hover glow */}
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                      style={{ background: `radial-gradient(circle at 30% 20%, ${service.color}20 0%, transparent 55%)` }}
                    />

                    <div className="relative z-10">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-8">
                        <div
                          className="w-16 h-16 rounded-2xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-3"
                          style={{ background: `${service.color}15`, border: `1px solid ${service.color}30` }}
                        >
                          <service.icon size={28} style={{ color: service.color }} />
                        </div>
                        <ArrowUpRight size={28} className="text-white/30 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
                      </div>

                      {/* Tagline */}
                      <span className="text-xs font-semibold uppercase tracking-[0.18em] mb-3 block" style={{ color: `${service.color}cc` }}>
                        {service.tagline}
                      </span>

                      {/* Title */}
                      <h3 className="text-2xl lg:text-3xl font-semibold text-white mb-4 leading-tight">
                        {service.title}
                      </h3>

                      {/* Description */}
                      <p className="text-white/55 text-base leading-relaxed mb-6">
                        {service.desc}
                      </p>

                      {/* Features */}
                      <div className="space-y-2.5 mb-7">
                        {service.features.map((feature, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <Check size={15} className="flex-shrink-0" style={{ color: service.color }} />
                            <span className="text-white/75 text-sm">{feature}</span>
                          </div>
                        ))}
                      </div>

                      {/* Custom packages line removed per user request - just show Learn More */}
                      <div className="flex items-center justify-end pt-5 border-t border-white/10">
                        <span className="inline-flex items-center gap-1.5 text-sm font-semibold" style={{ color: service.color }}>
                          {t.learnMore}
                          <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                        </span>
                      </div>
                    </div>

                    {/* Bottom accent line */}
                    <div
                      className="absolute bottom-0 left-0 right-0 h-[2px] opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                      style={{ background: `linear-gradient(90deg, transparent, ${service.color}80, transparent)` }}
                    />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* TRUSTED BY LOCAL BUSINESSES */}
      <Section className="pb-24 relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            variants={fadeInUp}
            className="relative rounded-3xl overflow-hidden border border-emerald-500/20 bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent"
            data-testid="trusted-by-section"
          >
            {/* Background glow */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: 'radial-gradient(ellipse at top left, rgba(16, 185, 129, 0.18) 0%, transparent 50%)' }}
            />

            <div className="relative z-10 p-10 lg:p-16">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                {/* Left: Text */}
                <div>
                  <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 mb-6">
                    <Award size={14} className="text-emerald-400" />
                    <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-emerald-400">
                      {t.trustedLabel}
                    </span>
                  </div>
                  <h2 className="text-3xl md:text-5xl font-semibold text-white mb-5 tracking-tight uppercase">
                    {t.trustedTitle}
                  </h2>
                  <p className="text-white/60 text-lg leading-relaxed">
                    {t.trustedDesc}
                  </p>
                </div>

                {/* Right: Stats */}
                <div className="grid grid-cols-2 gap-4">
                  {trustedStats.map((stat, i) => (
                    <motion.div
                      key={i}
                      variants={fadeInUp}
                      className="p-6 rounded-2xl bg-black/30 border border-white/[0.08] hover:border-emerald-500/30 transition-all duration-300"
                    >
                      <stat.icon size={20} className="text-emerald-400 mb-3" />
                      <div className="text-3xl lg:text-4xl font-bold text-white mb-1 tracking-tight">
                        {stat.value}
                      </div>
                      <div className="text-xs text-white/50 uppercase tracking-wider">
                        {stat.label}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* Why PIRONE */}
      <Section className="py-24 relative">
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div variants={fadeInUp} className="mb-14 max-w-2xl">
            <span className="inline-block text-[11px] font-mono uppercase tracking-[0.25em] text-emerald-400/70 mb-3">
              {t.whyLabel}
            </span>
            <h2 className="text-3xl md:text-5xl font-semibold text-white tracking-tight">
              {t.whyTitle}
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {whyFeatures.map((feature, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className="group p-6 rounded-2xl bg-[#0A0A0A]/60 border border-white/[0.06] hover:border-emerald-500/20 transition-all duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-emerald-500/5 border border-emerald-500/20 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon size={20} className="text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-white/45 text-sm leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* CTA */}
      <Section className="py-24 relative">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            variants={fadeInUp}
            className="relative p-12 lg:p-16 rounded-3xl bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20 text-center overflow-hidden"
          >
            <div
              className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] pointer-events-none"
              style={{ background: 'radial-gradient(ellipse at center, rgba(16, 185, 129, 0.15) 0%, transparent 70%)' }}
            />

            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-semibold text-white mb-4">
                {t.ctaTitle}
              </h2>
              <p className="text-white/55 text-lg mb-8 max-w-xl mx-auto">
                {t.ctaDesc}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/contact"
                  className="group inline-flex items-center gap-3 h-14 px-8 bg-emerald-400 text-black font-semibold rounded-full hover:bg-white transition-colors duration-300"
                  data-testid="services-cta-btn"
                >
                  <span>{t.ctaButton}</span>
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/new-jersey"
                  className="inline-flex items-center gap-2 h-14 px-8 bg-white/5 border border-white/10 text-white font-medium rounded-full hover:bg-white/10 transition-colors"
                  data-testid="services-cta-secondary"
                >
                  <span>{t.ctaSecondary}</span>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </Section>
    </div>
  );
};

export default ServicesPage;
